<template>
    <div class="component-gallery-carousel">
        <div class="carousel">
            <div
                ref="container"
                class="slides"
                @scroll="recalculateSlidesAndRescroll"
                @touchcancel="handleTouchEnd"
                @touchend="handleTouchEnd"
                @touchstart="handleTouchStart"
            >
                <div
                    v-for="(slide, index) in slidesEndless"
                    :key="slide.key"
                    ref="elements"
                    class="slide"
                >
                    <div
                        ref="itemsAnimated"
                        class="card-wrapper"
                    >
                        <GalleryCarouselCard
                            :data="slide.slide"
                            :has-eager-image="hasEagerImage && index < 3"
                            :sizes="['290px', [1360, '404px']]"
                        />
                    </div>
                </div>
            </div>
        </div>

        <ButtonRound
            class="button-round left"
            :aria-label="t('components.gallery_carousel.button_prev')"
            icon="angle-left"
            @click="previous"
        />

        <ButtonRound
            class="button-round right"
            :aria-label="t('components.gallery_carousel.button_next')"
            icon="angle-right"
            @click="next"
        />
    </div>

    <div class="dots">
        <div
            v-for="(_slide, index) in data.items"
            :key="index"
            :class="classDot(index)"
            class="dot"
            @click.prevent.stop="goToSlide(index)"
        ></div>
    </div>
</template>

<script setup lang="ts">
import { toRef } from 'vue'
import type { ComponentGalleryCarouselFragment } from '@/graphql/api'
import { useAnimationScale } from '@/components/layout/carousels/base/useAnimationScale'
import { useCarouselEndless } from '@/components/layout/carousels/base/useCarouselEndless'

import GalleryCarouselCard from '@/components/cms/component/carousel/card/GalleryCarouselCard.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    data: ComponentGalleryCarouselFragment
    hasEagerImage?: boolean
}>()

const { t } = useI18n()

const animationScale = useAnimationScale()

const {
    current,
    next,
    previous,
    slidesEndless,
    container,
    elements,
    itemsAnimated,
    recalculateSlidesAndRescroll,
    handleTouchStart,
    handleTouchEnd,
} = useCarouselEndless(
    true,
    toRef(() => props.data.items),
    animationScale
)

const classDot = (index: number) => ({
    active:
        slidesEndless.value[current.value || 0]?.slide.image.id ===
        props.data.items[index]?.image.id,
})

const goToSlide = (index: number) => {
    current.value +=
        index -
        props.data.items.findIndex(
            (item) =>
                item.image.id ===
                slidesEndless.value[current.value]?.slide.image.id
        )
}
</script>

<style lang="scss" scoped>
.component-gallery-carousel {
    position: relative;
    margin: auto;
    width: 100%;
    max-width: 1440px;
    display: flex;
    justify-content: center;

    .carousel {
        max-width: 100%;

        .slides {
            position: relative;
            width: 100%;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            .slide {
                width: 290px;
                height: 370px;
                position: relative;
                user-select: none;
                scroll-snap-align: center;
                flex-shrink: 0;

                @include min-width(1360) {
                    width: 404px;
                    height: 520px;
                }

                .card-wrapper {
                    width: 100%;
                    height: 100%;
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 25px);
        display: none;

        &.disabled {
            display: none;
        }

        &.left {
            left: 15px;
        }

        &.right {
            right: 15px;
        }

        @include min-width(1024) {
            display: block;
        }

        @include min-width(1440) {
            &.left {
                left: -20px;
            }

            &.right {
                right: -20px;
            }
        }
    }
}

.dots {
    margin-top: 16px;
    display: flex;
    justify-content: center;

    @include min-width(1024) {
        margin-top: 32px;
    }

    .dot {
        flex: 0 0 7px;
        height: 7px;
        background-color: $grey-200;
        margin: 4px;
        border-radius: 50%;
        cursor: pointer;
        transition: background-color 80ms ease-in-out;

        &.active {
            background-color: $blue;
        }
    }
}
</style>
